import type { UsageEntry } from '../../contracts';
import type { Evidence, Segment } from '../../types';
export const outputLabels = {
  review: '沟通线索',
  teaching: '教学观察',
  service: '服务建议',
  knowledge: '案例候选',
};
export type OutputCategory = keyof typeof outputLabels;
export interface RuleSnapshot {
  uuid: string;
  revision: number;
  title: string;
  keyword: string;
  exclusion: string;
}
export interface ProcessingSnapshot {
  uuid: string;
  revision: number;
  title: string;
  parallel: number;
  timeoutMs: number;
}
export interface PricingSnapshot {
  uuid: string;
  revision: number;
  publishedAt: string;
  centsPerMinute: number;
  expiresAt: string;
}
export interface TemplateSettings {
  scene: string;
  outputs: OutputCategory[];
  ruleUuids: string[];
  minMatches: number;
  prompt: string;
}
export interface TemplateVersion {
  uuid: string;
  version: number;
  createdAt: string;
  note: string;
  settings: TemplateSettings;
  rules: RuleSnapshot[];
  processing: ProcessingSnapshot;
}
export interface TemplateTest {
  rules?: RuleSnapshot[];
  processing?: ProcessingSnapshot;
  fingerprint: string;
  testedAt: string;
  recordingUuid: string;
  recordingRevision: number;
  transcriptVersionUuid: string;
  candidateCount: number;
  contributions: { ruleUuid: string; matched: number; excluded: number }[];
}
export interface AnalysisTemplate {
  draft: TemplateSettings;
  versions: TemplateVersion[];
  publishedVersionUuid?: string;
  test?: TemplateTest;
}
export interface FrozenRecording {
  uuid: string;
  revision: number;
  title: string;
  campus: string;
  memberUuid: string;
  transcriptVersionUuid: string;
  durationMs: number;
  scene: string;
  segments: Segment[];
}
export interface CandidateSuggestion {
  uuid: string;
  category: OutputCategory;
  title: string;
  observation: string;
  rule: RuleSnapshot;
  evidence: Evidence;
  segmentUuids: string[];
  missingInformation: string[];
}
export interface AnalysisResultVersion {
  uuid: string;
  version: number;
  createdAt: string;
  input: FrozenRecording;
  templateVersionUuid: string;
  processing: ProcessingSnapshot;
  rules: RuleSnapshot[];
  suggestions: CandidateSuggestion[];
  contributions: { ruleUuid: string; matched: number; excluded: number }[];
  limitations: string[];
}
export interface AnalysisItem {
  uuid: string;
  input: FrozenRecording;
  state: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  attempts: number;
  resultVersion: number;
  checkpoint: number;
  elapsedMs: number;
  failure?: string;
  simulateFailure: boolean;
  result?: AnalysisResultVersion;
}
export interface AnalysisTask {
  schema: 1;
  templateUuid: string;
  template: TemplateVersion;
  rules: RuleSnapshot[];
  processing: ProcessingSnapshot;
  pricing: PricingSnapshot;
  items: AnalysisItem[];
  generation: number;
  parentTaskUuid?: string;
  createdBy: string;
}
export type QualityTruth = 'positive' | 'negative' | 'unknown' | 'unlabelled';
export interface QualitySample {
  schema: 1;
  input: FrozenRecording;
  evidence: Evidence;
  truth: QualityTruth;
  labelNote: string;
  labelHistory: {
    at: string;
    truth: QualityTruth;
    note: string;
    actor: string;
  }[];
  subject: string;
  noise: string;
  participants: string;
}
export interface QualityPrediction {
  sampleUuid: string;
  truth: QualityTruth;
  predicted: boolean;
}
export interface ConfusionMatrix {
  tp: number;
  fp: number;
  fn: number;
  tn: number;
  excluded: number;
  total: number;
  precision: number | null;
  recall: number | null;
  accuracy: number | null;
}
export interface QualityReport {
  uuid: string;
  createdAt: string;
  sampleRevisions: Record<string, number>;
  samples: {
    uuid: string;
    truth: QualityTruth;
    subject: string;
    noise: string;
  }[];
  compared: {
    templateUuid: string;
    versionUuid: string;
    name: string;
    predictions: QualityPrediction[];
    matrix: ConfusionMatrix;
  }[];
  missesReviewed: boolean;
  coverageNote: string;
}
export interface AnalysisStore {
  confirmed?: Record<string, string>;
  feedback?: Record<string, string>;
  usage: UsageEntry[];
}
export const allScenes = [
  '全部场景',
  '课堂教学',
  '课后沟通',
  '教研复盘',
  '其他场景',
];
